import type { PlatformNotificationType, PlatformNotifyClients, PlatformNotifyPrefs } from "@cafe/shared";
import { Alert, Badge, Button, Card, Checkbox, dialogs, Field, PageHeader, SearchSelect, SegmentedControl, Skeleton, Switch, toast } from "@cafe/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, Unlink } from "lucide-react";
import { useEffect, useState } from "react";
import { errorText, http, ROLES } from "../lib/api";

type Settings = {
  prefs: PlatformNotifyPrefs;
  types: { type: PlatformNotificationType; label: string; hint: string; perCompany: boolean; onlyMine: boolean }[];
  chooseClients: boolean;
  telegram: { linked: false } | { linked: true; username: string | null; linkedAt: string };
  bot: string;
  botEnabled: boolean;
};

function TelegramCard({ s }: { s: Settings }) {
  const qc = useQueryClient();
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Пока ссылка открыта — ждём, когда бот подтвердит привязку
  useEffect(() => {
    if (!link || s.telegram.linked) return;
    const t = setInterval(() => void qc.invalidateQueries({ queryKey: ["my-notifications"] }), 3000);
    return () => clearInterval(t);
  }, [link, s.telegram.linked, qc]);
  useEffect(() => {
    if (s.telegram.linked) setLink(null);
  }, [s.telegram.linked]);
  const connect = async () => {
    setBusy(true);
    try {
      const r = await http.post<{ url: string }>("/me/telegram/link");
      setLink(r.url);
      window.open(r.url, "_blank", "noopener");
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    try {
      const r = await http.post<{ status: "sent" | "skipped" }>("/me/telegram/test");
      if (r.status === "sent") toast.success("Сообщение отправлено — проверьте Telegram");
      else toast.info("Бот не настроен на сервере — сообщение записано в журнал сервера");
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  const unlink = () => void dialogs.confirmDanger({
    title: "Отключить Telegram?", description: "Уведомления перестанут приходить. Подключить снова можно в любой момент.", confirmText: "Отключить",
    onConfirm: async () => {
      try {
        await http.del("/me/telegram");
        await qc.invalidateQueries({ queryKey: ["my-notifications"] });
      } catch (e) {
        toast.error(errorText(e));
      }
    },
  });
  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">Telegram</h2>
          <p className="text-sm text-muted">Бот @{s.bot} — тот же, что у клиентов. Сообщения приходят только вам.</p>
        </div>
        {s.telegram.linked ? <Badge tone="success" dot>Подключён{s.telegram.username ? ` · @${s.telegram.username}` : ""}</Badge> : <Badge tone="neutral" dot>Не подключён</Badge>}
      </div>
      {!s.botEnabled && <Alert tone="warning">На сервере не задан токен бота (TELEGRAM_BOT_TOKEN) — сообщения не отправляются, а пишутся в журнал сервера.</Alert>}
      {s.telegram.linked ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" leftIcon={<Send />} onClick={() => void test()}>Отправить проверку</Button>
          <Button variant="ghost" leftIcon={<Unlink />} onClick={unlink}>Отключить</Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Button className="self-start" leftIcon={<Send />} loading={busy} onClick={() => void connect()}>Подключить Telegram</Button>
          {link && (
            <p className="text-sm text-muted">
              В Telegram нажмите «Запустить» (Start). Не открылось? <a className="text-primary hover:underline" href={link} target="_blank" rel="noreferrer">Откройте ссылку</a> — она действует 15 минут.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

/** Мои уведомления в Telegram: что присылать и о каких клиентах. Состав событий — по роли, отдел продаж — только свои клиенты. */
export default function NotificationsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["my-notifications"], queryFn: () => http.get<Settings>("/me/notifications") });
  const managers = useQuery({
    queryKey: ["staff-options", "managers"], enabled: !!q.data?.chooseClients,
    queryFn: () => http.get<{ id: string; name: string; role: string }[]>("/staff/options", { managers: "1" }),
  });
  const [prefs, setPrefs] = useState<PlatformNotifyPrefs | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.data) setPrefs(q.data.prefs);
  }, [q.data]);
  const s = q.data;
  if (q.isError) return <><PageHeader title="Уведомления" /><Alert tone="danger">{errorText(q.error)}</Alert></>;
  if (!s || !prefs) return <><PageHeader title="Уведомления" /><Skeleton className="h-64" /></>;
  const dirty = JSON.stringify(prefs) !== JSON.stringify(s.prefs);
  const toggle = (t: PlatformNotificationType, on: boolean) => setPrefs({ ...prefs, muted: on ? prefs.muted.filter((x) => x !== t) : [...prefs.muted, t] });
  const save = async () => {
    setBusy(true);
    try {
      const r = await http.put<Settings>("/me/notifications", prefs);
      qc.setQueryData(["my-notifications"], r);
      toast.success("Настройки сохранены");
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const managerOptions = (managers.data ?? []).map((m) => ({ value: m.id, label: m.name, description: ROLES[m.role] ?? m.role }));
  return (
    <>
      <PageHeader title="Уведомления" description="Заявки, обращения, оплаты и другие события — сразу в Telegram." />
      <TelegramCard s={s} />
      <Card className="flex flex-col gap-4 p-5">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">Что присылать</h2>
          <p className="text-sm text-muted">Список зависит от вашей роли. О действиях, которые вы сделали сами, бот не пишет.</p>
        </div>
        <div className="flex flex-col divide-y divide-border">
          {s.types.map((t) => (
            <Switch key={t.type} className="py-3" checked={!prefs.muted.includes(t.type)} onCheckedChange={(v) => toggle(t.type, v)}
              label={t.label} description={t.onlyMine ? `${t.hint}. Только по вашим клиентам.` : t.hint} />
          ))}
        </div>
      </Card>
      <Card className="flex flex-col gap-4 p-5">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">О каких клиентах</h2>
          <p className="text-sm text-muted">Для обращений, оплат, новых клиентов и подписки. Заявки с сайта приходят всем, кто с ними работает.</p>
        </div>
        {s.chooseClients ? (
          <>
            <SegmentedControl<PlatformNotifyClients> aria-label="Клиенты" value={prefs.clients} onValueChange={(v) => setPrefs({ ...prefs, clients: v })}
              options={[{ value: "all", label: "Все клиенты" }, { value: "mine", label: "Мои клиенты" }, { value: "selected", label: "Клиенты менеджеров" }]} />
            {prefs.clients === "selected" && (
              <Field label="Менеджеры" hint="События клиентов, закреплённых за выбранными сотрудниками">
                <SearchSelect multiple value={prefs.managerIds} options={managerOptions} onValueChange={(v) => setPrefs({ ...prefs, managerIds: v })} placeholder="Выберите менеджеров" />
              </Field>
            )}
          </>
        ) : (
          <p className="text-sm">Вы получаете события только своих клиентов — тех, за кем вы закреплены менеджером.</p>
        )}
        {prefs.clients !== "all" && (
          <Checkbox checked={prefs.unassigned} onCheckedChange={(v) => setPrefs({ ...prefs, unassigned: v === true })}
            label="И клиентов без менеджера" description="Например, заведения, которые зарегистрировались сами и ещё ни за кем не закреплены" />
        )}
      </Card>
      <Button className="self-start" loading={busy} disabled={!dirty} onClick={() => void save()}>Сохранить</Button>
    </>
  );
}
